import React, { useState, useEffect } from 'react';
import { useAccount, useConnect, useDisconnect, useSwitchChain, useChainId } from 'wagmi';
import { Button } from '../ui/button';
import { Wallet, ChevronDown, AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const POLYGON_CHAIN_ID = 137;

export const ConnectWalletButton = ({ className }) => {
  const { address, isConnected } = useAccount();
  const { connect, connectors, isPending } = useConnect();
  const { disconnect } = useDisconnect();
  const { switchChain } = useSwitchChain();
  const chainId = useChainId();
  const [showMenu, setShowMenu] = useState(false);

  const isOnPolygon = chainId === POLYGON_CHAIN_ID;
  const shortAddr = address ? `${address.slice(0, 6)}...${address.slice(-4)}` : '';

  const handleConnect = () => {
    const injected = connectors.find((c) => c.id === 'injected') || connectors[0];
    if (!injected) return;
    connect({ connector: injected });
  };

  const handleSwitchToPolygon = () => {
    switchChain({ chainId: POLYGON_CHAIN_ID });
  };

  if (!isConnected) {
    return (
      <Button
        data-testid="connect-wallet-btn"
        onClick={handleConnect}
        disabled={isPending}
        className={cn(
          'bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/25',
          className
        )}
      >
        <Wallet className="w-4 h-4 mr-2" />
        {isPending ? 'Conectando...' : 'Conectar Wallet'}
      </Button>
    );
  }

  return (
    <div className="relative">
      <Button
        data-testid="wallet-status-btn"
        variant="outline"
        onClick={() => setShowMenu((s) => !s)}
        className={cn(
          'border-amber-500/30 text-amber-100 hover:bg-amber-500/10',
          !isOnPolygon && 'border-red-500/40 text-red-300 hover:bg-red-500/10',
          className
        )}
      >
        {isOnPolygon ? (
          <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" />
        ) : (
          <AlertCircle className="w-4 h-4 mr-2 text-red-400" />
        )}
        {shortAddr}
        <ChevronDown className="w-4 h-4 ml-2" />
      </Button>
      {showMenu && (
        <div
          data-testid="wallet-menu"
          className="absolute right-0 mt-2 w-72 z-50 bg-zinc-900 border border-amber-500/20 rounded-xl shadow-2xl shadow-black/50 p-3 space-y-2"
        >
          <div className="text-xs text-white/50 uppercase tracking-wide px-2">Wallet</div>
          <div className="px-2 py-1 text-sm font-mono text-amber-300 break-all">{address}</div>

          {!isOnPolygon ? (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <div className="text-xs text-red-300 mb-2 flex items-center gap-2">
                <AlertCircle className="w-3.5 h-3.5" />
                Red incorrecta. Cambia a Polygon.
              </div>
              <Button
                data-testid="switch-to-polygon-btn"
                size="sm"
                onClick={handleSwitchToPolygon}
                className="w-full bg-amber-500 text-black hover:bg-amber-400"
              >
                Cambiar a Polygon
              </Button>
            </div>
          ) : (
            <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Conectado a Polygon
            </div>
          )}

          <Button
            data-testid="disconnect-wallet-btn"
            variant="ghost"
            size="sm"
            onClick={() => {
              disconnect();
              setShowMenu(false);
            }}
            className="w-full text-red-400 hover:bg-red-500/10"
          >
            Desconectar
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConnectWalletButton;
